import React, { useCallback, useMemo } from 'react';

import { useLocation } from 'react-router-dom';

import { Autocomplete, Box, TextField } from '@mui/material';

import * as libRegions from '@luxuryescapes/lib-regions';

import useAppNavigation from '~/hooks/useAppNavigation';

import { addQuery } from '~/utils/url';

import PageHeader from '../../Common/Elements/PageHeader';

type Props = {
  user: App.User | null;
  currency: string;
  regions: Array<libRegions.Region>;
};

export default function CreditsCurrencySelector(props: Props) {
  const { user, currency, regions } = props;

  const location = useLocation();
  const { push } = useAppNavigation();

  const currencyOptions = useMemo(() => regions.filter((region) => Boolean(region.currencyCode)), [regions]);

  const selectedCurrency = useMemo(
    () => currencyOptions.find((region) => region.currencyCode === currency) ?? null,
    [currency, currencyOptions],
  );

  const handleCurrencyChange = useCallback(
    (_event: React.SyntheticEvent, region: libRegions.Region | null) => {
      if (!region?.currencyCode || region.currencyCode === currency) {
        return;
      }

      const newLocation = addQuery(location, { currency: region.currencyCode });
      push(newLocation);
    },
    [currency, location, push],
  );

  return (
    <PageHeader title={user ? `View credits / ${user.fullName}` : 'View user credits'}>
      <Box width={250}>
        <Autocomplete
          value={selectedCurrency}
          options={currencyOptions}
          onChange={handleCurrencyChange}
          getOptionLabel={(region) => `${region.name} (${region.currencyCode})`}
          isOptionEqualToValue={(option, value) =>
            option.name === value.name && option.currencyCode === value.currencyCode
          }
          autoHighlight
          disableClearable
          renderInput={(params) => <TextField {...params} label="Region and currency" variant="outlined" />}
        />
      </Box>
    </PageHeader>
  );
}
